import { useMemo, useState } from 'react';
import { articles, categories } from '@/mocks/melons';

interface Props {
  activeCat: string;
  setActiveCat: (id: string) => void;
}

const tagStyle: Record<string, string> = {
  爆: 'bg-primary-600 text-background-50',
  热: 'bg-accent-500 text-background-50',
  新: 'bg-secondary-600 text-background-50',
  盘点: 'bg-foreground-800 text-background-50',
  合集: 'bg-primary-600 text-background-50',
};

export default function Featured({ activeCat, setActiveCat }: Props) {
  const [keyword, setKeyword] = useState('');

  const list = useMemo(() => {
    let base = activeCat && activeCat !== 'all' ? articles.filter((a) => a.categoryId === activeCat) : articles.filter((a) => a.series === 'featured');
    const kw = keyword.trim().toLowerCase();
    if (kw) {
      base = base.filter((a) => a.title.toLowerCase().includes(kw) || a.summary.toLowerCase().includes(kw) || a.host.toLowerCase().includes(kw) || a.categoryName.toLowerCase().includes(kw));
    }
    return base.slice(0, 9);
  }, [activeCat, keyword]);

  return (
    <section id="featured" className="scroll-mt-20 border-b border-background-200 bg-background-50 py-12 md:py-16">
      <div className="container-page">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-col items-start gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-medium tracking-wide text-primary-700">
              <i className="ri-vip-diamond-line" aria-hidden="true" />
              精选瓜料 · 编辑部推荐
            </span>
            <h2 className="text-2xl font-bold text-foreground-950 md:text-[34px]">
              本周精品瓜<span className="ml-2 text-primary-600">精选合集</span>
            </h2>
          </div>

          <div className="flex w-full max-w-sm items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2.5 lg:mb-1">
            <i className="ri-search-line text-foreground-500" aria-hidden="true" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜瓜料标题、主播或事件…"
              aria-label="搜索瓜料"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </div>
        </div>

        <div className="scroll-hidden mt-6 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="栏目筛选">
          <button
            type="button"
            onClick={() => setActiveCat('all')}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              activeCat === 'all' ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            全部
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveCat(c.id)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                activeCat === c.id ? 'bg-foreground-900 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
            </span>
            <p className="mt-4 text-base font-semibold text-foreground-800">没找到相关瓜料</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词或栏目试试，或去瓜田盘点补齐进度</p>
            <button type="button" onClick={() => { setKeyword(''); setActiveCat('all'); }} className="mt-5 rounded-full bg-primary-600 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700">
              重置筛选
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((a) => (
              <a
                key={a.id}
                href={`/post/${a.id}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-background-200 bg-background-100 transition-all hover:-translate-y-1 hover:border-primary-200 hover:bg-background-50"
              >
                <div className="relative overflow-hidden">
                  <img src={a.image} alt={a.title} loading="lazy" className="h-44 w-full object-cover object-top transition-transform duration-500 group-hover:scale-105 md:h-52" />
                  <span className={`absolute left-3 top-3 inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-bold ${tagStyle[a.tagLabel] ?? 'bg-foreground-800 text-background-50'}`}>
                    {a.tagLabel}
                  </span>
                  <span className="absolute bottom-3 right-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-semibold text-foreground-700 backdrop-blur">
                    {a.categoryName}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h3 className="font-heading text-[17px] font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                    {a.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 flex-1 text-[13px] leading-relaxed text-foreground-600">{a.summary}</p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="inline-flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true" />{a.viewsText}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-heart-3-line" aria-hidden="true" />{a.likesText}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-chat-3-line" aria-hidden="true" />{a.commentsText}</span>
                    <span className="inline-flex items-center gap-1"><i className="ri-time-line" aria-hidden="true" />{a.readMin} 分钟</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}